import React from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import { useBoolean } from 'usehooks-ts';

import Button from 'components/App/Button';

import { ReactComponent as UpIcon } from 'assets/images/up.svg';
import { ReactComponent as DownIcon } from 'assets/images/down.svg';
import { ReactComponent as ChevronDownIcon } from 'assets/images/chevron-down.svg';

import cn from 'utils/classnames';
import { DAY_MILESTONES } from 'constants/analytics';
import { formatNumber } from 'utils/currencyFormat';

import styles from './StayTable.module.scss';

function StayTable({ data, dataTime, compareWith = 'SAME_SEGMENT' }) {
  const { value: isOpen, toggle: onToggle } = useBoolean(false);

  const formattedData = React.useMemo(() => {
    let unit = 'days';
    const nowMoment = moment();
    let numberOfTimelines = dataTime;

    if (dataTime === DAY_MILESTONES.DAY_365) {
      unit = 'months';
      numberOfTimelines = 12; // 12 months
    }

    const currentList = data?.data || (Array.isArray(data) ? data : []);
    const compareList = data?.compareData || [];

    return Array.from({ length: numberOfTimelines }, (_, index) => {
      const itemMoment = nowMoment
        .clone()
        .subtract(index, unit);

      const nights = currentList.reduce((total, item) => {
        const dataMoment = moment(item.startedAt);

        const isBetween = dataMoment.isBetween(
          itemMoment.clone().startOf(unit),
          itemMoment.clone().endOf(unit),
          undefined,
          '[]',
        );

        if (isBetween) {
          return total + (item.nights || 0);
        }

        return total;
      }, 0);

      const compareUnitMoment = compareWith === 'LAST_YEAR'
        ? itemMoment.clone().subtract(1, 'year')
        : itemMoment;

      const compareNights = compareList.reduce((total, item) => {
        const dataMoment = moment(item.startedAt);

        const isBetween = dataMoment.isBetween(
          compareUnitMoment.clone().startOf(unit),
          compareUnitMoment.clone().endOf(unit),
          undefined,
          '[]',
        );

        if (isBetween) {
          return total + (item.nights || 0);
        }

        return total;
      }, 0);

      // Percentage difference calculation
      let percentDiff = 0;
      let status = 'DOWN';

      if (compareNights === 0) {
        if (nights > 0) {
          percentDiff = 100;
          status = 'UP';
        } else {
          percentDiff = 0;
          status = 'DOWN';
        }
      } else {
        const diff = ((nights - compareNights) / compareNights) * 100;
        percentDiff = Math.round(diff * 10) / 10;
        status = percentDiff >= 0 ? 'UP' : 'DOWN';
      }

      return {
        nights,
        percentDiff,
        status,
        date: itemMoment.toISOString(false),
      };
    });
  }, [data, dataTime, compareWith]);

  const dateFormat = React.useMemo(() => {
    switch (dataTime) {
      case DAY_MILESTONES.DAY_365:
        return '[Tháng] MM [năm] YYYY';

      default:
        return 'D [tháng] MM [năm] YYYY';
    }
  }, [dataTime]);

  return (
    <div className={styles.Component}>
      <Button
        color={null}
        classes={cn(styles.Button, isOpen && styles.ButtonCollapsed)}
        onClick={onToggle}
      >
        <span>Cho xem dữ liệu theo bảng</span>

        <ChevronDownIcon
          width={20}
          height={20}
          className={cn(styles.Chevron, isOpen && styles.ChevronCollapsed)}
        />
      </Button>

      <div className={cn(styles.TableWrap, !isOpen && styles.Collapsed)}>
        <table className={styles.Table}>
          <colgroup>
            <col span={1} />
            <col span={1} />
            {/* <col span={1} /> */}
          </colgroup>

          <thead className={styles.TableHeading}>
            <tr className={styles.TableHeadingRow}>
              <th>Tháng</th>
              <th>Đêm phòng</th>
              <th>% Chênh lệch</th>
            </tr>
          </thead>

          <tbody className={styles.TableBody}>
            {formattedData.map((item, index) => (
              <tr key={item.date || index} className={styles.TableBodyRow}>
                <td>{moment(item.date).format(dateFormat)}</td>

                <td>{formatNumber(item.nights)}</td>

                <td>
                  <div>
                    <p>{`${item.percentDiff >= 0 ? '+' : ''}${formatNumber(item.percentDiff)}%`}</p>

                    {item.status === 'UP' ? <UpIcon /> : <DownIcon />}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

StayTable.propTypes = {
  data: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.object,
  ]).isRequired,
  dataTime: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  compareWith: PropTypes.string,
};

StayTable.defaultProps = {
  compareWith: 'SAME_SEGMENT',
};

export default React.memo(StayTable);
